import { Suspense } from 'react'
import { Outlet, createFileRoute, redirect } from '@tanstack/react-router'
import { AppSidebar } from '@/components/shell/app-sidebar'
import { AppTopBar } from '@/components/shell/app-topbar'
import { SidebarProvider } from '@/components/shell/sidebar-context'
import { WorkspaceProvider } from '@/components/workspace/workspace-provider'
import { WorkspaceDnd } from '@/components/workspace/workspace-dnd'
import { OrgShellSkeleton } from '@/components/shell/screen-skeletons'
import { currentUser } from '@/lib/auth/session'
import { orgScanTotalsQuery } from '@/lib/queries/analytics'
import { novaLinkTemplateQuery } from '@/lib/queries/app-settings'
import { activeQrCodesQuery, groupsQuery } from '@/lib/queries/qr-codes'
import { fetchOrganizationBySlug } from '@/lib/queries/organizations'
import { fetchSession } from '@/lib/queries/session'
import { orgRoutes } from '@/lib/routes'

export const Route = createFileRoute('/o/$org')({
  loader: async ({ context: { queryClient }, params: { org: slug } }) => {
    const user = await currentUser()

    /* The root gate already required a session, so reaching here without one
       means it died in between. Bounce rather than render a shell with no data
       behind it. */
    if (!user) {
      throw redirect({ to: '/login', search: { next: `/o/${slug}/qr-codes` } })
    }

    /*
     * Awaited, unlike the prefetches below: the organization id is needed to
     * build the other query keys, so everything downstream depends on these
     * two. They are independent of each other, so they run together.
     *
     * The slug is resolved against the DATABASE rather than against the
     * session's membership list, and that distinction is the fix for a real
     * bug. The membership list is the right answer for the org switcher and the
     * wrong one for "which workspace is this URL about": a platform
     * administrator holds IAM grants over every organization and a membership
     * row in none of them, so matching the slug against `session.organizations`
     * failed for every org but their own, and the fallback below then sent them
     * back to it. That presented as the admin directory being unclickable —
     * every row bounced straight home.
     */
    const [session, organization] = await Promise.all([
      fetchSession(queryClient, user.id),
      fetchOrganizationBySlug(queryClient, slug),
    ])

    /* Nothing came back: either no such organization, or the IAM policies
       won't show this caller the one that exists. Usually a stale bookmark from
       before a rename, or an organization someone was removed from — landing
       them on one they can actually see beats a 404. */
    if (!organization) {
      const fallback = session.organizations[0]
      if (fallback) throw redirect({ ...orgRoutes(fallback.slug).qrCodes })
      throw new Error('This account does not belong to any organization.')
    }

    /*
     * Deliberately NOT awaited, and deliberately not `ensureQueryData`. These
     * four are what the shell and the first screen read, and starting them here
     * rather than at mount is what removes the request waterfall — but blocking
     * the route transition on them would trade the skeleton for a blank screen.
     * They resolve into the same cache the components suspend on.
     */
    void queryClient.prefetchQuery(groupsQuery(organization.id))
    void queryClient.prefetchQuery(activeQrCodesQuery(organization.id))
    void queryClient.prefetchQuery(novaLinkTemplateQuery())
    void queryClient.prefetchQuery(orgScanTotalsQuery(organization.id))

    return { userId: user.id }
  },

  pendingComponent: OrgShellSkeleton,
  component: OrgLayout,
})

function OrgLayout() {
  const { userId } = Route.useLoaderData()
  const { org: slug } = Route.useParams()

  /*
   * The Suspense boundary wraps the PROVIDER, and that placement is the whole
   * point.
   *
   * `WorkspaceProvider` is what suspends — it holds the session, groups, codes
   * and link-template queries — and it is rendered BY this route. The route's
   * own `pendingComponent` covers the LOADER, so it is already gone by the time
   * this renders and never sees that suspension. Under the App Router the same
   * trap wore a different hat: a `loading.tsx` in this segment is the fallback
   * for the layout's CHILDREN, so it also missed it — the file existed, looked
   * correct, and rendered nothing at all on a cold load. Only a boundary here
   * catches it, which a slow-server test is what established.
   */
  return (
    <Suspense fallback={<OrgShellSkeleton />}>
      <WorkspaceProvider userId={userId} orgSlug={slug}>
        {/* Inside the provider (drops read and write the workspace), outside
            the sidebar/main split (drags cross it). */}
        <WorkspaceDnd>
          <SidebarProvider>
            <div className="bg-background flex h-dvh overflow-hidden">
              <AppSidebar />
              <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
                <AppTopBar />
                <main className="flex min-h-0 flex-1 flex-col overflow-hidden">
                  <Outlet />
                </main>
              </div>
            </div>
          </SidebarProvider>
        </WorkspaceDnd>
      </WorkspaceProvider>
    </Suspense>
  )
}
